import * as React from 'react';
import { DialogMainStyles, dialogButtonStyles } from '@fluidx/office-fluid-types';
import { Dialog, DialogFooter, DialogType, DefaultButton, PrimaryButton } from 'office-ui-fabric-react';
import { HostAppColors, getDialogTitleStyle, getPrimaryButtonStyle } from '../../styles';

export interface ErrorDialogStrings {
  /** Title of the error dialog */
  dialogTitle: string;

  /** A user friendly error message */
  dialogMessage: string;
}

export interface ErrorDialogProps {
  /** Strings to render in the error dialog */
  dialogStrings: ErrorDialogStrings;

  /** The app colors of the host */
  appColors?: HostAppColors;

  /** Action buttons to render in the dialog footer */
  actions?: ErrorDialogActionButton[];

  /** Debug info shown in dev mode  */
  debugInfo?: ErrorDialogDebugProps;
}

export interface ErrorDialogActionButton {
  /** Type of fluentui button to render as dialog action button */
  type: 'Primary' | 'Default';

  /** Text to render on dialog action button */
  label: string;

  /** On button click, this action is executed in addition to dialog dismissal logic */
  action?: () => void;
}

export interface ErrorDialogDebugProps {
  /** An error code that signifies what caused the error, only in debug builds */
  debugErrorCode?: string;

  /** The exact error with message and stack */
  debugError?: any;

  /** Component Stack caught by the error boundary */
  debugReactErrorBoundaryInfo?: React.ErrorInfo;
}

export const ErrorDialog = React.memo((props: ErrorDialogProps) => {
  const { dialogStrings, actions, appColors, debugInfo } = props;

  const [dialogHidden, setDialogHidden] = React.useState<boolean>(false);
  const [actionButtons, setActionButtons] = React.useState<JSX.Element[]>([]);

  React.useEffect(() => {
    if (actions) {
      setActionButtons(
        actions?.map(({ type, label, action }) => {
          const onActionButtonClick = () => {
            action && action();
            setDialogHidden(true);
          };

          if (type === 'Primary') {
            return (
              <PrimaryButton
                label={`primaryAction-${label}`}
                onClick={onActionButtonClick}
                style={dialogButtonStyles}
                className={appColors && getPrimaryButtonStyle(appColors)}
              >
                {label}
              </PrimaryButton>
            );
          }

          return (
            <DefaultButton key={`secondaryAction-${label}`} onClick={onActionButtonClick} style={dialogButtonStyles}>
              {label}
            </DefaultButton>
          );
        })
      );
    }
  }, [actions]);

  return (
    <Dialog
      hidden={dialogHidden}
      maxWidth="none"
      dialogContentProps={{
        type: DialogType.normal,
        title: dialogStrings.dialogTitle,
        styles: appColors ? getDialogTitleStyle(appColors) : undefined
      }}
      styles={DialogMainStyles}
    >
      <div>{dialogStrings.dialogMessage}</div>

      {!isProduction && debugInfo && <ErrorDialogDebug {...debugInfo} />}

      {actions && <DialogFooter>{actionButtons}</DialogFooter>}
    </Dialog>
  );
});

const isProduction = process.env.NODE_ENV === 'production';

const ErrorDialogDebug = React.memo((props: ErrorDialogDebugProps) => {
  // Since this is debug only, keeping the strings as is
  return (
    <>
      <br />
      {(props.debugError || props.debugErrorCode || props.debugReactErrorBoundaryInfo) && (
        <div style={{ fontWeight: 600 }}>Debug Info:</div>
      )}
      <br />
      {props.debugErrorCode && <div>ErrorCode: {props.debugErrorCode}</div>}
      {
        <pre style={{ fontFamily: 'Segoe UI' }}>
          {(props.debugError && props.debugError.stack) || JSON.stringify(props.debugError)}
        </pre>
      }
      {props.debugReactErrorBoundaryInfo && (
        <pre style={{ fontFamily: 'Segoe UI' }}>
          Component Stack:
          {props.debugReactErrorBoundaryInfo && props.debugReactErrorBoundaryInfo.componentStack}
        </pre>
      )}
    </>
  );
});
